<template>
  <div
    class="ooloja-card-default__body bg-white p-6 rounded-lg shadow-sm border border-gray-200"
  >
    <div class="flex flex-col md:flex-row items-center justify-between mb-4">
      <div class="text-center md:text-left">
        <h3 class="text-lg font-semibold text-gray-800">
          Top 10 Ticket Médio por Cliente
        </h3>
        <small>Dados são dos ultimos 12 meses</small>
      </div>
      <div class="flex items-center space-x-2">
        <button
          @click="exportToCSV"
          class="bg-green-500 text-white text-sm font-medium py-1.5 px-3 rounded hover:bg-green-600 transition-colors"
        >
          Exportar CSV
        </button>
        <button
          @click="exportToPDF"
          class="bg-red-500 text-white text-sm font-medium py-1.5 px-3 rounded hover:bg-red-600 transition-colors"
        >
          Exportar PDF
        </button>
      </div>
    </div>
    <div class="grid grid-cols-1 lg:grid-cols-2 gap-3">
      <div class="max-h-[300px] overflow-y-auto">
        <table class="w-full border border-gray-200 rounded-lg shadow-sm table-fixed">
          <thead
            class="bg-gray-100 text-gray-800 font-semibold text-sm sticky top-0 z-10"
          >
            <tr>
              <th class="py-1 px-4 text-left">Cliente</th>
              <th class="py-1 px-4 text-right w-[150px]">
                Receita Total
                <button
                  @click="sortBy('receita_total')"
                  class="ml-1 text-gray-600 hover:text-gray-800"
                >
                  <span
                    :class="{
                      'text-blue-600': sortKey === 'receita_total' && sortOrder === 'asc',
                    }"
                    >↑</span
                  >
                  <span
                    :class="{
                      'text-blue-600':
                        sortKey === 'receita_total' && sortOrder === 'desc',
                    }"
                    >↓</span
                  >
                </button>
              </th>
              <th class="py-1 px-4 text-center w-[150px]">
                Pedidos
                <button
                  @click="sortBy('quantidade_total')"
                  class="ml-1 text-gray-600 hover:text-gray-800"
                >
                  <span
                    :class="{
                      'text-blue-600':
                        sortKey === 'quantidade_total' && sortOrder === 'asc',
                    }"
                    >↑</span
                  >
                  <span
                    :class="{
                      'text-blue-600':
                        sortKey === 'quantidade_total' && sortOrder === 'desc',
                    }"
                    >↓</span
                  >
                </button>
              </th>
              <th class="py-1 px-4 text-right w-[150px]">
                Ticket Médio
                <button
                  @click="sortBy('ticket_medio')"
                  class="ml-1 text-gray-600 hover:text-gray-800"
                >
                  <span
                    :class="{
                      'text-blue-600': sortKey === 'ticket_medio' && sortOrder === 'asc',
                    }"
                    >↑</span
                  >
                  <span
                    :class="{
                      'text-blue-600': sortKey === 'ticket_medio' && sortOrder === 'desc',
                    }"
                    >↓</span
                  >
                </button>
              </th>
            </tr>
          </thead>
          <tbody class="bg-white">
            <tr
              v-for="(item, index) in sortedData"
              :key="item.user_id"
              :class="{
                'bg-gray-50': index % 2 === 0,
                'bg-white': index % 2 !== 0,
                'hover:bg-gray-100': true,
              }"
              class="transition-colors duration-200"
            >
              <td class="py-2 px-4 text-gray-700 text-sm truncate">
                {{ item.id }} {{ item.nome_cliente }}
              </td>
              <td class="py-2 px-4 text-right text-gray-600 text-sm">
                {{ formatCurrency(item.receita_total) }}
              </td>
              <td class="py-2 px-4 text-center text-gray-600 text-sm">
                {{ item.total_pedidos }}
              </td>
              <td class="py-2 px-4 text-right text-gray-600 text-sm">
                {{ formatCurrency(item.ticket_medio) }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div class="mb-6">
        <Bar :data="chartData" :options="chartOptions" class="h-64" />
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from "vue";
import { Bar } from "vue-chartjs";
import {
  Chart as ChartJS,
  Title,
  Tooltip,
  Legend,
  BarElement,
  CategoryScale,
  LinearScale,
} from "chart.js";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

ChartJS.register(Title, Tooltip, Legend, BarElement, CategoryScale, LinearScale);

const props = defineProps({
  ticketMedioData: {
    type: Array,
    required: true,
  },
});

const sortKey = ref("ticket_medio");
const sortOrder = ref("desc");

// Formatar valores monetários
const formatCurrency = (value) => {
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(value);
};

// Ordenação
const sortedData = computed(() => {
  const sorted = [...props.ticketMedioData];
  if (sortKey.value) {
    sorted.sort((a, b) => {
      const valueA = a[sortKey.value];
      const valueB = b[sortKey.value];
      if (sortOrder.value === "asc") {
        return valueA > valueB ? 1 : -1;
      }
      return valueA < valueB ? 1 : -1;
    });
  }
  return sorted;
});

const sortBy = (key) => {
  if (sortKey.value === key) {
    sortOrder.value = sortOrder.value === "asc" ? "desc" : "asc";
  } else {
    sortKey.value = key;
    sortOrder.value = "asc";
  }
};

// Dados do gráfico
const chartData = computed(() => ({
  labels: props.ticketMedioData.map(
    (item) =>
      item.nome_cliente.slice(0, 20) + (item.nome_cliente.length > 20 ? "..." : "")
  ),
  datasets: [
    {
      label: "Ticket Médio (R$)",
      data: props.ticketMedioData.map((item) => item.ticket_medio),
      backgroundColor: "rgba(59, 130, 246, 0.5)",
      borderColor: "rgba(59, 130, 246, 1)",
      borderWidth: 1,
    },
  ],
}));

const chartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      display: true,
      position: "top",
    },
    tooltip: {
      callbacks: {
        label: (context) => `${context.dataset.label}: ${formatCurrency(context.raw)}`,
      },
    },
  },
  scales: {
    y: {
      beginAtZero: true,
      ticks: {
        callback: (value) => formatCurrency(value),
      },
    },
    x: {
      ticks: {
        autoSkip: false,
        maxRotation: 45,
        minRotation: 45,
      },
    },
  },
};

// Exportar para CSV
const exportToCSV = () => {
  const headers = ["Nome", "Receita Total", "Pedidos", "Ticket Médio"];
  const data = props.ticketMedioData.map((item) => [
    item.nome_cliente,
    formatCurrency(item.receita_total),
    item.total_pedidos,
    formatCurrency(item.ticket_medio),
  ]);

  const csvContent = [
    headers.join(","),
    ...data.map((row) => row.map((cell) => `"${cell}"`).join(",")),
  ].join("\n");

  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = "ticket_medio_clientes.csv";
  link.click();
};

// Exportar para PDF
const exportToPDF = async () => {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.width;
  const pageHeight = doc.internal.pageSize.height;
  const margin = 14;
  const systemVersion = "Versão 1.0"; // Substitua pela versão real
  const website = "www.ooloja.com.br";

  // Formatar data e hora atual
  const now = new Date();
  const dateText = now.toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });

  // Carregar logo
  let logoImg = null;
  try {
    const logoUrl = "/ooadmin.png";
    const response = await fetch(logoUrl);
    if (!response.ok) throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    const blob = await response.blob();
    const reader = new FileReader();
    reader.readAsDataURL(blob);
    await new Promise((resolve) => (reader.onload = resolve));
    logoImg = reader.result;
  } catch (error) {
    console.error("Erro ao carregar logo:", error.message);
  }

  // Configurar tabela
  autoTable(doc, {
    head: [["Produto", "Receita Total", "Quantidade Total", "Ticket Médio"]],
    body: props.ticketMedioData.map((item) => [
      item.nome_cliente,
      formatCurrency(item.receita_total),
      item.total_pedidos,
      formatCurrency(item.ticket_medio),
    ]),
    startY: 30,
    styles: { fontSize: 10, cellPadding: 3 },
    headStyles: { fillColor: [100, 116, 139], textColor: [255, 255, 255] },
    alternateRowStyles: { fillColor: [243, 244, 246] },
    didDrawPage: (data) => {
      // Data e hora
      doc.setFont("Times", "normal");
      doc.setFontSize(10);
      doc.setTextColor(100, 100, 100);
      doc.text(`Gerado em: ${dateText}`, margin, 15);

      // Cabeçalho
      doc.setFontSize(15);
      doc.setTextColor(0, 0, 0);
      doc.text("Ticket Médio por Cliente", margin, 22);
      if (logoImg) {
        doc.addImage(logoImg, "PNG", pageWidth - 40 - margin, 7, 40, 15);
      } else {
        doc.setFontSize(8);
        doc.text("[Logo]", pageWidth - 30 - margin, 15);
      }
      // Rodapé
      doc.setFontSize(8);
      doc.setTextColor(100, 100, 100);
      const footerText = `${website} - ${systemVersion}`;
      doc.text(footerText, margin, pageHeight - 10);
      const pageStr = `Página ${data.pageNumber} de ${doc.internal.getNumberOfPages()}`;
      doc.text(pageStr, pageWidth - margin - doc.getTextWidth(pageStr), pageHeight - 10);
    },
    margin: { top: 30 },
  });

  doc.save("ticket_medio_clientes.pdf");
};
</script>

<style scoped>
.table-container {
  @apply mt-4 overflow-x-auto;
}

table {
  @apply w-full border-collapse;
}

th,
td {
  @apply border border-gray-200;
}

.table-container::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

.table-container::-webkit-scrollbar-track {
  @apply bg-gray-100;
}

.table-container::-webkit-scrollbar-thumb {
  @apply bg-gray-400 rounded;
}

.table-container::-webkit-scrollbar-thumb:hover {
  @apply bg-gray-500;
}
</style>
